import React, { useState, ChangeEvent, useRef, useEffect } from "react";
import Papa from "papaparse";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TitleWithDivider } from "@common/TitleWithDivider";
import { ConfirmCancelButtons } from "@components/Buttons/ConfirmCancelButtons";
import FailedUploadsModal from "@components/Modals/FailedUploadsModal";
import { USER } from "@types/user";
import Encoding from "encoding-japanese";
import { error_message } from "@constants/Errors";
import messages from "@constants/Messages";
import BadFieldsModal from "@components/Modals/BadFieldsModal";

interface CsvPageProps {
  uploadHandler: () => void;
  cancelText?: string;
  titleText?: string;
  failedUsers?: [];
  onCancel: () => void;
  sampleData: USER[];
  templateName: string;
  csvData: any[];
  setCsvData: (data: any[]) => void;
  headerMapping: [];
  fromPage?: string;
}

export const CsvUploader: React.FC<CsvPageProps> = ({
  uploadHandler,
  cancelText = "Cancel",
  titleText = "CSVアップロード",
  failedUsers = [],
  onCancel,
  templateName,
  sampleData,
  csvData,
  setCsvData,
  headerMapping,
  fromPage = "",
}) => {
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [failedUsersModalOpen, setFailedUsersOpen] = useState(false);
  const [failedRowsModalOpen, setFailedRowsModalOpen] = useState(false);

  const [csvFileSubmitted, setCsvFileSubmitted] = useState(false);

  const [unTranslatedData, setUnTranslatedData] = useState([]);

  const [files, setFiles] = useState<File[]>([]);
  const [fileReadText, setFileReadText] = useState(
    error_message.csv.no_file_selected,
  );

  const [originPage, setOriginPage] = useState(fromPage);
  const [internal, setInternal] = useState(
    fromPage === "contact" ? true : false,
  );

  const errorToast = (response) => toast.error(`${response}`);
  const [failedUsersArray, setFailedUsersArray] = useState(failedUsers);

  const headerError = () => toast.error(error_message.csv.incorrect_headers);
  const noCsvImportError = () => toast.error(error_message.csv.no_data);
  const [nullErrors, setNullErrors] = useState([]);

  const createReverseMapping = (mapping: { [key: string]: string }) => {
    return Object.fromEntries(
      Object.entries(mapping).map(([key, value]) => [value, key]),
    );
  };

  const resetFields = () => {
    setNullErrors([]);
    setFailedUsersArray([]);
    setCsvData([]);
    setFileReadText(error_message.csv.no_file_selected);
  };

  const reverseHeaderMapping = createReverseMapping(headerMapping);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNullErrors([]);
    setFailedUsersArray([]);
    const selectedFiles = Array.from(event.target.files || []);
    setFiles(selectedFiles);

    if (selectedFiles.length > 0) {
      const file = selectedFiles[0];
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);

      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        const encoding = Encoding.detect(uint8Array);

        let decodedCsv;
        if (encoding === "SJIS" || encoding === "SHIFT_JIS") {
          const unicodeArray = Encoding.convert(uint8Array, {
            from: "SJIS",
            to: "UNICODE",
            type: "array",
          });
          decodedCsv = Encoding.codeToString(unicodeArray);
        } else {
          // Force decode as UTF-8 when encoding is NOT Shift_JIS
          decodedCsv = new TextDecoder("utf-8").decode(uint8Array);
        }

        Papa.parse(decodedCsv, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const parsedHeaders = results.meta.fields;

            const expectedHeadersFromMapping = Object.values(headerMapping);

            const unmatchedHeaders = parsedHeaders?.filter(
              (header) => !expectedHeadersFromMapping.includes(header),
            );

            if (unmatchedHeaders && unmatchedHeaders.length > 0) {
              console.warn("unmatched headers found in CSV:", unmatchedHeaders);
              headerError();
              setCsvData([]);
              return;
            }

            setUnTranslatedData(results.data);

            if (results.data.length > 10000) {
              toast.error("CSVアップロードは10,000件以下としてください");
              return;
            }

            const translatedData = results.data.map(
              (row: any, index: number) => {
                const translatedRow: { [key: string]: any } = { index };
                parsedHeaders.forEach((header) => {
                  const englishHeader = reverseHeaderMapping[header] || header;
                  translatedRow[englishHeader] = row[header] || "";
                });
                return translatedRow;
              },
            );

            setCsvData(translatedData);
            setFileReadText(`「${file.name}」が選択されています。`);
          },
          error: (error) => {
            errorToast(error_message.csv.parse_error);
            setCsvData([]);
            setFiles([]);
          },
        });
      };

      reader.onerror = () => {
        console.error(error_message.csv.loading);
        errorToast(error_message.csv.loading);
      };
    }

    event.target.value = null;
  };

  const validateNullFields = (user, index) => {
    const errors: { row: number; error: string }[] = [];
    const excludedKeys = [
      "id",
      "index",
      "利用アプリ（KAKELY：1、YOBELY：2）",
      "氏名（姓）",
      "氏名（名）",
      "かな（せい）（任意）",
      "かな（めい）（任意）",
      "社員番号（任意）",
      "メールアドレス（任意）",
      "メールアドレス１（任意）",
      "メールアドレス２（任意）",
      "役職（任意）",
      "電話番号１",
      "電話番号（任意）",
      "電話番号２（任意）",
      "部署コード（任意）",
      "部署・役職（任意）",
      "支社名 （任意）",
      "部署・役職（任意）",
      "支社電話番号 （任意）",
      "支社郵便番号 （任意）",
      "支社FAX番号 （任意）",
      "支社住所 （任意）",
      "支社ホームページ （任意）",
      "企業名 （任意）",
      "会社名 （任意）",
      "会社名 （任意）",
      "会社電話番号 （任意）",
      "会社FAX番号 （任意）",
      "会社住所 （任意）",
      "会社郵便番号 （任意）",
      "その他情報（任意）",
      "会社ホームページ （任意）",
      "企業電話番号 （任意）",
      "一斉発信グループコード（任意）",
      "企業郵便番号 （任意）",
      "企業FAX番号 （任意）",
      "電話番号（任意）",
      "電話番号１（任意）",
      "電話番号 2（任意）",
      "企業住所 （任意）",
      "企業ホームページ （任意）",
      "削除フラグ",
      "パスワード",
      "親部署コード（任意）",
      "その他情報（任意）",
      "部署名かな（任意）",
      "部署概要（任意）",
    ];

    let userIsContact = false;
    const roleToCheck = "権限（1：一般者、2：管理者、3：連絡先）";
    const keysToSkip = ["id", "電話番号", "ユーザーID", "ログインパスワード"];

    if (user["削除フラグ"] == 1) {
      return null;
    }

    Object.keys(user).forEach((key) => {
      if (user[roleToCheck] === "3") {
        userIsContact = true;
      }
      if (userIsContact && keysToSkip.includes(key)) {
        return;
      }
      if (
        (!user[key] || user[key].toString().trim() === "") &&
        !excludedKeys.includes(key)
      ) {
        errors.push({
          row: index + 1,
          error: `${key}が必要です`,
        });
      }
    });
    return errors.length > 0 ? errors : null;
  };

  const translateSampleData = (
    sampleData: USER[],
    mapping: { [key: string]: string },
  ) => {
    return sampleData.map((row) => {
      const translatedRow: { [key: string]: any } = {};

      Object.keys(mapping).forEach((key) => {
        if (key in row) {
          translatedRow[mapping[key]] = row[key];
        }
      });

      return translatedRow;
    });
  };

  const handleDownloadSampleCsv = () => {
    const translatedSampleData = translateSampleData(sampleData, headerMapping);
    const filteredHeaders = Object.values(headerMapping);

    const csvString = Papa.unparse(translatedSampleData, {
      columns: filteredHeaders,
    });

    const utf8WithBom = new Uint8Array([
      0xef,
      0xbb,
      0xbf,
      ...new TextEncoder().encode(csvString),
    ]);
    const blob = new Blob([utf8WithBom], { type: "text/csv;charset=utf-8;" });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${templateName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const removeEntryFromCsv = (originalIndex) => {
    setCsvData((prev) => {
      return prev.filter((user) => user.index !== originalIndex);
    });
  };

  const handleUpload = () => {
    const nullField = unTranslatedData.map((row: any, index: number) => {
      const errorRow = validateNullFields(row, index);

      setNullErrors((prevErrors: any) => {
        return errorRow ? [...prevErrors, ...errorRow] : prevErrors;
      });

      return errorRow ? true : false;
    });

    if (nullField.includes(true)) {
      errorToast(error_message.csv.null_fields);
      return;
    }

    uploadHandler();
    setCsvFileSubmitted(true);
  };

  useEffect(() => {
    setFailedUsersArray(failedUsers);
  }, [failedUsers]);

  useEffect(() => {
    if (csvData.length <= 0) {
      setFileReadText(error_message.csv.no_file_selected);

      if (csvFileSubmitted) {
        resetFields();
        setCsvFileSubmitted(false);
      }
    }
  }, [csvData]);

  return (
    <div>
      <TitleWithDivider titleText={titleText} useDivider={true} />
      <div className="mt-4 flex justify-between">
        <button
          className="p-3 mb-5 bg-button-light-blue text-white rounded-lg hover:opacity-80"
          onClick={handleDownloadSampleCsv}
        >
          テンプレートCSVをダウンロード
        </button>
        {failedUsersArray.length > 0 && (
          <button
            className="ml-5 mb-2 pl-2 pr-2 items-center justify-center bg-button-light-blue text-white rounded-xl hover:opacity-80"
            onClick={() => setFailedUsersOpen(true)}
          >
            {originPage === "user"
              ? "失敗したユーザーを表示"
              : originPage === "contact"
              ? "失敗した電話帳を表示"
              : originPage === "department"
              ? "失敗した部署を表示"
              : ""}
          </button>
        )}
        {nullErrors?.length > 0 && (
          <button
            className="ml-5 mb-2 pl-2 pr-2 items-center justify-center bg-button-light-blue text-white rounded-xl hover:opacity-80"
            onClick={() => setFailedRowsModalOpen(true)}
          >
            失敗した行を表示
          </button>
        )}
      </div>
      <div
        className={`flex flex-col p-5 bg-white rounded-lg border border-divider ${
          csvData.length > 0 ? "min-h-30" : ""
        }`}
      >
        <div className="flex items-start sm:items-center">
          <button
            className="mt-2 sm:mt-0 p-4 bg-button-light-blue text-sm sm:text-base text-white rounded-xl hover:opacity-80"
            onClick={() => fileInput.current?.click()}
          >
            ファイルを選択
          </button>
          <div className="p-5 bg-white rounded-xl mr-5 max-w-full break-words whitespace-pre-line">
            {fileReadText}
          </div>
          <input
            type="file"
            onChange={handleFileChange}
            accept=".csv"
            ref={fileInput}
            style={{ display: "none" }}
          />
        </div>
      </div>
      <div className="mb-5 sm:mb-1">
        <ConfirmCancelButtons
          confirmText={"インポート"}
          onConfirm={csvData.length === 0 ? noCsvImportError : handleUpload}
          onCancel={onCancel}
        />
      </div>

      {originPage === "user" ? (
        <div>
          <div>
            <h3 className="text-lg font-bold underline">
              {messages.csv.app_instruction_title}
            </h3>
            <p className="mt-2 text-sm">
              {messages.csv.warning_using_old_files}
            </p>
            <p className="mt-2 text-sm">{messages.csv.hint}</p>
            <p className="ml-10 mt-5 text-sm">{messages.csv.hint_example}</p>
            <p className="ml-18 text-sm whitespace-pre">
              {messages.csv.available_apps}
            </p>
            <p className="mt-2 text-sm">{messages.csv.set_zero_create_user}</p>
            <p className="mt-2 text-sm">{messages.csv.id_exists_auto_update}</p>
            <p className="mt-2 text-sm">{messages.csv.delete_on_flag}</p>
            <p className="mt-2 text-sm">{messages.csv.headers_on_first_line}</p>
            <p className="mt-2 text-sm">{messages.csv.second_line_sample}</p>
            <br />
          </div>

          <div>
            <h3 className="text-lg font-bold underline">
              {messages.csv.user_instruction_title}
            </h3>
            <p className="mt-2 text-sm">
              {messages.csv.instructions_csv_create_edit_user}
            </p>
            <p className="mt-2 text-sm">
              {messages.csv.instructions_opening_csv_file}
            </p>
            <p className="mt-2 text-sm">
              {messages.csv.instructions_opening_csv_file_cont}
            </p>
            <p className="mt-2 text-sm">
              {messages.csv.instructions_csv_version_warning}
            </p>

            <p className="text-red-500 text-lg underline mt-5">
              {messages.csv.users_warning}
            </p>
          </div>
        </div>
      ) : fromPage === "contact" ? (
        <div>
          <h3 className="text-lg font-bold underline">
            {internal
              ? messages.csv.contacts_instruction_title_internal
              : messages.csv.contacts_instruction_title}
          </h3>
          <p className="mt-2 text-sm">{messages.csv.warning_using_old_files}</p>
          <p className="mt-2 text-sm">{messages.csv.set_zero_create_user}</p>
          <p className="mt-2 text-sm">{messages.csv.id_exists_auto_update}</p>
          <p className="mt-2 text-sm">{messages.csv.delete_on_flag}</p>
          <p className="mt-2 text-sm">{messages.csv.headers_on_first_line}</p>
          <p className="mt-2 text-sm">{messages.csv.second_line_sample}</p>

          <br />
          <h3 className="text-lg font-bold underline">
            {messages.csv.user_instruction_title}
          </h3>

          <p className="mt-2 text-sm">
            {messages.csv.instructions_csv_create_edit_user}
          </p>
          <p className="mt-2 text-sm">
            {messages.csv.instructions_opening_csv_file}
          </p>
          <p className="mt-2 text-sm">
            {messages.csv.instructions_opening_csv_file_cont}
          </p>
          <p className="mt-2 text-sm">
            {messages.csv.instructions_csv_version_warning}
          </p>
          <p className="text-red-500 text-lg underline mt-5">
            {internal
              ? messages.csv.contacts_warning_internal
              : messages.csv.contacts_warning}
          </p>
        </div>
      ) : fromPage === "department" ? (
        <div>
          <h3 className="text-lg font-bold underline">
            {messages.csv.department_instruction_title}
          </h3>
          <p className="mt-2 text-sm">{messages.csv.warning_using_old_files}</p>
          <p className="mt-2 text-sm">
            {messages.csv.instructions_csv_create_edit_user}
          </p>
          <p className="mt-2 text-sm">
            {messages.csv.instructions_opening_csv_file}
          </p>
          <p className="mt-2 text-sm">
            {messages.csv.instructions_opening_csv_file_cont}
          </p>
          <p className="mt-2 text-sm">
            {messages.csv.instructions_csv_version_warning}
          </p>
          <br />
          <p className="mt-2 text-sm">
            {messages.csv.set_zero_create_department}
          </p>
          <p className="mt-2 text-sm">
            {messages.csv.delete_department_on_flag}
          </p>
          <p className="mt-2 text-sm">
            {messages.csv.set_department_code_to_link_parents}
          </p>
          <p className="mt-2 text-sm">{messages.csv.headers_on_first_line}</p>
          <p className="mt-2 text-sm">{messages.csv.second_line_sample}</p>
          <p className="text-red-500 text-lg underline mt-5">
            {messages.csv.departments_warning}
          </p>
        </div>
      ) : null}

      <FailedUploadsModal
        removeFromCsv={removeEntryFromCsv}
        failedUploads={failedUsersArray}
        confirmText={""}
        isOpen={failedUsersModalOpen}
        fromPage={originPage}
        onRequestClose={() => setFailedUsersOpen(false)}
      />
      <BadFieldsModal
        badFields={nullErrors}
        confirmText={""}
        isOpen={failedRowsModalOpen}
        onRequestClose={() => setFailedRowsModalOpen(false)}
      />
    </div>
  );
};
